import React, { useState } from "react";
import { Card, Button, Typography, Tag, Space, Divider } from "antd";

const { Title, Text } = Typography;

const CARD_WIDTH = 384;
const CARD_HEIGHT = 1349;
const PRIMARY_COLOR = "#673de6";

const PricingPlans = () => {
  const [selected, setSelected] = useState("Business");
  const [openPlan, setOpenPlan] = useState(null);

  const toggleFeatures = (title) => {
    setOpenPlan(openPlan === title ? null : title);
  };

  const plans = [
    {
      title: "Premium",
      desc: "Everything you need to create your website.",
      price: "$2.99",
      old: "$11.99",
      save: "Save 75%",
      renew: "$7.99/mo when you renew",
      popular: false,
      features: {
        main: [
          "100 websites",
          "~25,000 visits monthly",
          "100 GB SSD storage",
          "400,000 files & directories (inodes)",
          "Free pre-built templates",
          "Free automatic website migration",
          "Unlimited free SSL",
          "Free email",
        ],
        extra: [
          "Weekly backups",
          "WordPress vulnerabilities scanner",
          "WordPress auto updates",
          "Standard WordPress acceleration",
          "Unlimited bandwidth",
          "Free domain ($9.99 value)",
          "Free CDN",
          "WordPress AI tools",
          "WordPress Staging tool",
          "Dedicated IP address",
          "Priority support",
        ],
      },
    },
    {
      title: "Business",
      desc: "Level up with more power and enhanced features.",
      price: "$3.99",
      old: "$13.99",
      save: "Save 71%",
      renew: "$8.99/mo when you renew",
      popular: true,
      features: {
        main: [
          "100 websites",
          "~100,000 visits monthly",
          "200 GB NVMe storage",
          "600,000 files & directories (inodes)",
          "Free pre-built templates",
          "Free automatic website migration",
          "Unlimited free SSL",
          "Free email",
        ],
        extra: [
          "Daily & on-demand backups",
          "WordPress Vulnerabilities scanner",
          "Smart WordPress auto updates",
          "Advanced WordPress acceleration",
          "Unlimited bandwidth",
          "Free domain ($9.99 value)",
          "Free CDN",
          "WordPress AI tools",
          "Dedicated IP address",
          "Priority support",
        ],
      },
    },
    {
      title: "Cloud Startup",
      desc: "Enjoy optimized performance & powerful resources.",
      price: "$7.99",
      old: "$24.99",
      save: "Save 68%",
      renew: "$19.99/mo when you renew",
      popular: false,
      features: {
        main: [
          "300 websites",
          "~200,000 visits monthly",
          "200 GB NVMe storage",
          "2,000,000 files & directories (inodes)",
          "Free pre-built templates",
          "Free automatic website migration",
          "Unlimited free SSL",
          "Free email",
        ],
        extra: [
          "Daily & on-demand backups",
          "WordPress vulnerabilities scanner",
          "Smart WordPress auto updates",
          "Advanced WordPress acceleration",
          "Unlimited bandwidth",
          "Free domain ($9.99 value)",
          "Free CDN",
          "WordPress AI tools",
          "WordPress staging tool",
          "Dedicated IP address",
          "Priority support",
        ],
      },
    },
  ];

  return (
    <div style={{ padding: "80px 24px", background: "#fff" }}>
      {/* TITLE */}
      <Space direction="vertical" align="center" style={{ width: "100%" }}>
        <Title level={2}>Pick Your Perfect Plan</Title>
        <Text type="secondary">
          Get started in complete confidence. 30-day money-back guarantee.
        </Text>
      </Space>

      {/* CARD CONTAINER */}
      <div
        style={{
          marginTop: 48,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ display: "flex", width: CARD_WIDTH * 3 }}>
          {plans.map((plan) => {
            const isSelected = selected === plan.title;
            const isOpen = openPlan === plan.title;

            return (
              <Card
                key={plan.title}
                hoverable
                style={{
                  width: CARD_WIDTH,
                  height: CARD_HEIGHT,
                  borderRadius: 14,
                  border: isSelected
                    ? `2px solid ${PRIMARY_COLOR}`
                    : "1px solid #e5e7eb",
                  boxShadow: isSelected
                    ? "0 20px 40px rgba(103,61,230,0.25)"
                    : "none",
                }}
                bodyStyle={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {plan.popular && (
                  <Tag
                    color="purple"
                    style={{
                      position: "absolute",
                      top: -14,
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontWeight: 600,
                    }}
                  >
                    MOST POPULAR
                  </Tag>
                )}

                <Space direction="vertical" size={6}>
                  <Title level={4}>{plan.title}</Title>
                  <Text type="secondary">{plan.desc}</Text>

                  <Space size={8} style={{ marginTop: 16 }}>
                    <Text delete>{plan.old}</Text>
                    <Tag color="blue">{plan.save}</Tag>
                  </Space>

                  <Title level={2} style={{ color: PRIMARY_COLOR, margin: 0 }}>
                    {plan.price}
                    <Text type="secondary"> /mo</Text>
                  </Title>

                  <Text type="secondary">For 48-month term</Text>
                  <Text style={{ color: PRIMARY_COLOR, fontWeight: 500 }}>
                    + 3 months free
                  </Text>

                  {/* BUTTON IS NOW THE ONLY CLICKABLE PART */}
                  <Button
                    size="large"
                    block
                    style={{
                      marginTop: 16,
                      backgroundColor: isSelected ? PRIMARY_COLOR : "#fff",
                      borderColor: PRIMARY_COLOR,
                      color: isSelected ? "#fff" : PRIMARY_COLOR,
                      fontWeight: 500,
                    }}
                    onClick={() => setSelected(plan.title)}
                  >
                    {isSelected ? "Selected" : "Choose plan"}
                  </Button>

                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {plan.renew}
                  </Text>
                </Space>

                <Divider />

                <div style={{ flex: 1 }}>
                  <Space direction="vertical" size={8}>
                    {plan.features.main.map((f, i) => (
                      <Text key={i}>✔ {f}</Text>
                    ))}
                    {isOpen &&
                      plan.features.extra.map((f, i) => (
                        <Text key={i}>✔ {f}</Text>
                      ))}
                  </Space>
                </div>

                <Divider />

                <Button
                  type="link"
                  block
                  onClick={() => toggleFeatures(plan.title)}
                >
                  {isOpen ? "Hide features" : "See all features"}
                </Button>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PricingPlans;
